import {
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import { useJevCallDetail } from "./jevCallQueries";
import { isDropped, verdictText, verdictTone } from "./jevVerdict";
import {
  byLikelihood,
  duration,
  percent,
  readableEvidence,
} from "./traceFormat";
import type { JevAnswer, JevVerdict } from "./types";

type BoxAnswer = Extract<JevAnswer, { box: unknown }>;
type NoulAnswer = Extract<JevAnswer, { noul: unknown }>;

const isBox = (answer: JevAnswer): answer is BoxAnswer => "box" in answer;
const isNoul = (answer: JevAnswer): answer is NoulAnswer => "noul" in answer;

/** brian `room_roster_packet.staff_photo_asset`: never copied into traces. */
const isStaffPhoto = (name: string) => /^(ref|gallery)_/.test(name);

/** brian `check_packet`: the Noul `check_<kind>_<n>` asks about `crop_<kind>_<n>`. */
const cropFor = (answerName: string) =>
  answerName.startsWith("check_") ? `crop_${answerName.slice(6)}` : null;

const fields = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** brian `jev_tick_replay._pair_verdict`: a merged pair is a duplicate. */
const pairText = ({ value, threshold }: JevVerdict, merged: boolean) =>
  value == null
    ? "NOT MERGED · no answer"
    : `${merged ? "MERGED" : "NOT MERGED"} · same body ${value.toFixed(2)} ${merged ? "≥" : "<"} ${threshold?.toFixed(2) ?? "?"}`;

const VerdictBadge = ({
  verdict,
  pair,
}: {
  verdict: JevVerdict | undefined;
  pair: boolean;
}) => {
  if (!verdict) return null;
  const dropped = isDropped(verdict);
  return (
    <span
      className={`rounded-sm px-1.5 font-mono text-fine-print ${verdictTone(dropped)}`}
    >
      {pair
        ? pairText(verdict, dropped)
        : dropped
          ? `DROPPED · ${verdictText(verdict)}`
          : "KEPT"}
    </span>
  );
};

const Criterion = ({ value }: { value: unknown }) => {
  if (typeof value === "string")
    return <span className="text-annotation"> · {value}</span>;
  const examples = fields(value).examples;
  if (!Array.isArray(examples) || examples.length === 0) return null;
  const names = examples.map(String);
  return (
    <span className="text-annotation">
      {" "}
      · {names.length} reference photo{names.length > 1 ? "s" : ""} (
      {names.length > 1 ? `${names[0]}–${names[names.length - 1]}` : names[0]})
    </span>
  );
};

const AnswerBlock = ({
  name,
  answer,
  question,
  verdict,
  pair,
  reading,
  labels,
  crop,
}: {
  name: string;
  answer: JevAnswer;
  question: unknown;
  verdict: JevVerdict | undefined;
  pair: boolean;
  reading: string | undefined;
  labels: Record<string, string>;
  crop: { name: string; url: string | null } | undefined;
}) => {
  const { instructions } = fields(question);
  const criteria = fields(fields(question).criteria);
  const body = (
    <div className="min-w-0 space-y-1">
      <p className="flex flex-wrap items-center gap-2 font-mono text-xs font-medium text-ink">
        {name}
        <VerdictBadge verdict={verdict} pair={pair} />
      </p>
      {reading && <p className="text-xs text-ink-body">{reading}</p>}
      {typeof instructions === "string" && (
        <p className="text-xs text-annotation">{instructions}</p>
      )}
      {isBox(answer) ? (
        <p className="font-mono text-xs text-ink-body">
          box {answer.box.left.toFixed(2)}, {answer.box.top.toFixed(2)} –{" "}
          {answer.box.right.toFixed(2)}, {answer.box.bottom.toFixed(2)}
          {answer.p != null && ` · ${percent(answer.p)}`}
        </p>
      ) : isNoul(answer) ? (
        <p className="font-mono text-xs text-ink-body">
          {percent(answer.noul)} true
        </p>
      ) : (
        <ul className="space-y-0.5">
          {byLikelihood(
            answer.probabilities ?? { [answer.choice]: answer.p },
          ).map(([choice, p]) => (
            <li
              key={choice}
              className="grid grid-cols-[4rem_minmax(0,1fr)] gap-2 text-xs"
            >
              <span className="text-right font-mono text-ink-body">
                {percent(p)}
              </span>
              <span className="min-w-0 break-words">
                {labels[choice] && (
                  <span className="mr-1.5 font-medium text-ink">
                    {labels[choice]}
                  </span>
                )}
                <span
                  className={`font-mono ${labels[choice] ? "text-annotation" : "text-ink"}`}
                >
                  {choice}
                </span>
                <Criterion value={criteria[choice]} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  if (!crop) return body;
  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-3">
      {crop.url ? (
        <img
          src={crop.url}
          alt={`${crop.name}, as sent to Jev`}
          className="h-28 w-24 rounded-sm border border-rule bg-wall object-contain"
        />
      ) : (
        <EmptyNote>Crop not kept.</EmptyNote>
      )}
      {body}
    </div>
  );
};

const Sent = ({ label, value }: { label: string; value: unknown }) => {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="rounded-paper border border-rule bg-card p-3 text-xs text-annotation"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer font-medium text-ink-body">
        {label}
      </summary>
      {open && (
        <pre className="mt-2 max-h-96 overflow-auto font-mono text-xs break-all whitespace-pre-wrap text-ink-body">
          {readableEvidence(value)}
        </pre>
      )}
    </details>
  );
};

const Picture = ({
  name,
  url,
  caption,
  boxes,
  verdicts,
}: {
  name: string;
  url: string | null;
  caption: string | undefined;
  boxes: [string, BoxAnswer][];
  verdicts: Record<string, JevVerdict>;
}) => (
  <figure className="min-w-0">
    {url ? (
      <div className="relative">
        <img
          src={url}
          alt={`${name}, as sent to Jev`}
          className="block w-full rounded-paper border border-rule"
        />
        {boxes.map(([label, { box }]) => {
          const dropped = isDropped(verdicts[label]);
          return (
            <span
              key={label}
              className={`absolute border-2 ${dropped ? "border-dashed border-ink-declined" : "border-accent"}`}
              style={{
                left: `${box.left * 100}%`,
                top: `${box.top * 100}%`,
                width: `${(box.right - box.left) * 100}%`,
                height: `${(box.bottom - box.top) * 100}%`,
              }}
            >
              <span
                className={`absolute left-0 px-1 font-mono text-fine-print text-paper ${dropped ? "bottom-0 bg-ink-declined" : "top-0 bg-ink"}`}
              >
                {label}
              </span>
            </span>
          );
        })}
      </div>
    ) : (
      <EmptyNote>Picture not kept.</EmptyNote>
    )}
    <figcaption className="mt-1 font-mono text-xs text-annotation">
      {caption ?? name}
    </figcaption>
  </figure>
);

export const JevCallDetail = ({
  callUuid,
  active,
}: {
  callUuid: string;
  active: boolean;
}) => {
  const query = useJevCallDetail(callUuid, active);
  if (query.isPending) return <SkeletonStack count={3} className="p-5" />;
  if (query.isError)
    return (
      <PanelError
        label="this Jev call"
        onRetry={() => void query.refetch()}
        className="min-h-40"
      />
    );
  const call = query.data;
  const answers = Object.entries(call.answers ?? {});
  // A locate call's box is a fraction of its own crop as sent (padding and
  // all); any other is drawn on the picture the state names.
  const boxed = fields(call.state).picture ?? call.images[0]?.name;
  const pictureOf = (name: string) => call.pictures[name] ?? boxed;
  const boxesOn = (picture: string) =>
    answers.filter(
      (entry): entry is [string, BoxAnswer] =>
        isBox(entry[1]) && pictureOf(entry[0]) === picture,
    );
  const located = Object.keys(call.pictures).length > 0;
  const pair = call.question === "same_body";
  const answerBlock = ([name, answer]: [string, JevAnswer]) => {
    const crop = cropFor(name);
    const image = crop ? crops.get(crop) : undefined;
    return (
      <AnswerBlock
        key={name}
        name={name}
        answer={answer}
        question={fields(call.questions)[name]}
        verdict={call.verdicts[name]}
        pair={pair}
        reading={call.readings[name]}
        labels={call.labels}
        crop={crop ? { name: crop, url: image?.url ?? null } : undefined}
      />
    );
  };
  const crops = new Map(
    answers
      .map(([name]) => cropFor(name))
      .filter((crop): crop is string => crop !== null)
      .map((crop) => [crop, call.images.find((i) => i.name === crop)]),
  );
  const unpictured = answers.filter(([name]) => !call.pictures[name]);
  const staffPhotos = call.images.filter(({ name }) => isStaffPhoto(name));
  const pictures = call.images.filter(
    ({ name }) => !isStaffPhoto(name) && !crops.has(name),
  );
  return (
    <div
      className="space-y-4 border-t border-rule bg-wall p-4 sm:p-5"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-xs text-annotation">
        <span>Call {call.uuid}</span>
        <span>
          {call.model ?? "Model not recorded"} · {duration(call.ms)}
        </span>
      </div>
      {call.request_id && (
        <p className="font-mono text-xs text-annotation">
          TypeSafe request {call.request_id}
        </p>
      )}
      {call.error && (
        <p className="rounded-paper border border-rule bg-card p-3 font-mono text-xs break-words text-ink-body">
          {call.error}
        </p>
      )}
      <section className="space-y-2">
        <p className="text-mono-caps text-xs text-annotation">
          Pictures Jev was sent
        </p>
        {located && (
          <p className="text-xs text-annotation">
            Each picture is shown with the answers about it.
          </p>
        )}
        {located &&
          pictures.map(({ name, url }) => (
            <div
              key={name}
              className="grid grid-cols-1 gap-3 rounded-paper border border-rule bg-card p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
            >
              <Picture
                name={name}
                url={url}
                caption={call.captions[name]}
                boxes={boxesOn(name)}
                verdicts={call.verdicts}
              />
              <div className="min-w-0 space-y-3">
                {answers
                  .filter(([answer]) => call.pictures[answer] === name)
                  .map(answerBlock)}
              </div>
            </div>
          ))}
        {!located && pictures.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pictures.map(({ name, url }) => (
              <Picture
                key={name}
                name={name}
                url={url}
                caption={call.captions[name]}
                boxes={boxesOn(name)}
                verdicts={call.verdicts}
              />
            ))}
          </div>
        )}
        {crops.size > 0 && (
          <p className="text-xs text-annotation">
            Each crop is shown beside its answer below.
          </p>
        )}
        {staffPhotos.length > 0 && (
          <p className="font-mono text-xs text-annotation">
            {staffPhotos.length} staff reference photo
            {staffPhotos.length > 1 ? "s" : ""} (
            {staffPhotos.map(({ name }) => name).join(", ")}), not copied into
            traces
          </p>
        )}
        {call.still_url && (
          <a
            href={call.still_url}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-xs text-ink-body underline"
          >
            Camera still as captured (before masking)
          </a>
        )}
      </section>
      {unpictured.length > 0 && (
        <section className="space-y-3">
          <p className="text-mono-caps text-xs text-annotation">Answers</p>
          {unpictured.map(answerBlock)}
        </section>
      )}
      <Sent label="State sent" value={call.state} />
      {call.notes != null && (
        <Sent label="Notes (not sent to Jev)" value={call.notes} />
      )}
      <Sent label="Questions sent" value={call.questions} />
    </div>
  );
};
